import { useEffect, useRef, useState } from 'react';

interface CursorPosition {
  x: number;
  y: number;
}

interface CursorTrail {
  x: number;
  y: number;
  opacity: number;
  id: number;
}

interface UseCursorReturn {
  position: CursorPosition;
  trails: CursorTrail[];
  isMoving: boolean;
  velocity: number;
}

export const useCursor = (): UseCursorReturn => {
  const [position, setPosition] = useState<CursorPosition>({ x: 0, y: 0 });
  const [trails, setTrails] = useState<CursorTrail[]>([]);
  const [isMoving, setIsMoving] = useState(false);
  const [velocity, setVelocity] = useState(0);
  
  const lastPosition = useRef<CursorPosition>({ x: 0, y: 0 });
  const lastTime = useRef<number>(Date.now());
  const animationFrame = useRef<number>();
  const trailId = useRef<number>(0);
  const movingTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const currentTime = Date.now();
      const deltaTime = currentTime - lastTime.current;
      
      if (deltaTime > 0) {
        const deltaX = e.clientX - lastPosition.current.x;
        const deltaY = e.clientY - lastPosition.current.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const currentVelocity = distance / deltaTime;
        
        setVelocity(currentVelocity);
        setPosition({ x: e.clientX, y: e.clientY });
        setIsMoving(true);
        
        // Clear existing timeout
        if (movingTimeout.current) {
          clearTimeout(movingTimeout.current);
        }
        
        // Set moving to false after 50ms of no movement
        movingTimeout.current = setTimeout(() => {
          setIsMoving(false);
        }, 50);
        
        // Create trail points for fast movement
        if (currentVelocity > 0.5) {
          const newTrail: CursorTrail = {
            x: lastPosition.current.x,
            y: lastPosition.current.y,
            opacity: Math.min(currentVelocity * 0.8, 1),
            id: trailId.current++
          };
          
          setTrails(prev => [...prev, newTrail].slice(-15)); // Keep last 15 trails
        }
        
        lastPosition.current = { x: e.clientX, y: e.clientY };
        lastTime.current = currentTime;
      }
    };

    const updateTrails = () => {
      setTrails(prev => 
        prev
          .map(trail => ({ ...trail, opacity: trail.opacity * 0.92 }))
          .filter(trail => trail.opacity > 0.02)
      );
      
      animationFrame.current = requestAnimationFrame(updateTrails);
    };

    document.addEventListener('mousemove', handleMouseMove);
    animationFrame.current = requestAnimationFrame(updateTrails);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      if (movingTimeout.current) {
        clearTimeout(movingTimeout.current);
      }
    };
  }, []);

  return { position, trails, isMoving, velocity };
};

export default useCursor;